import { useCallback, useEffect } from "react";
import yaml from "js-yaml";
import { logError, logWarn, logInfo } from "./utils/logger";

const COMPONENT_NAME = 'AiFunctionHelper';

export class AiFunctionHelper {
  static getYamlDiffLines(oldYaml, newYaml) {
    const oldLines = oldYaml ? oldYaml.trim().split('\n') : [];
    const newLines = newYaml.trim().split('\n');
    const maxLen = Math.max(oldLines.length, newLines.length);
    const diffLines = [];

    logInfo(`Generating diff: ${oldLines.length} old lines vs ${newLines.length} new lines`, COMPONENT_NAME);

    for (let i = 0; i < maxLen; i++) {
      const oldLine = oldLines[i] || '';
      const newLine = newLines[i] || '';

      if (oldLine === newLine) {
        diffLines.push({ type: 'same', content: newLine });
      } else if (!oldLine && newLine) {
        diffLines.push({ type: 'added', content: newLine });
      } else if (oldLine && !newLine) {
        diffLines.push({ type: 'removed', content: oldLine });
      } else {
        diffLines.push({
          type: 'changed',
          oldContent: oldLine,
          newContent: newLine,
        });
      }
    }

    logInfo(`Diff generated: ${diffLines.length} total lines`, COMPONENT_NAME);
    return diffLines;
  }

  static handleAiResponse(
    aiMessage,
    currentModelYaml,
    setTextContent,
    setYamlDiff,
    setAiYamlHistory
  ) {
    setTextContent(aiMessage);
    
    let isYaml = false;
    try {
      const parsed = yaml.load(aiMessage);
      isYaml = parsed && typeof parsed === "object";
    } catch (error) {
    }

    const finalYaml = sessionStorage.getItem('finalYaml');

    if (finalYaml !== null && finalYaml !== undefined) {
      currentModelYaml = finalYaml;
    }

    if (isYaml) {
      const diff = AiFunctionHelper.getYamlDiffLines(currentModelYaml, aiMessage);
      setYamlDiff(diff);
      setAiYamlHistory((history) => [...history, { text: aiMessage, time: Date.now() }]);
    } else {
      setYamlDiff([]);
    }
  }

  static handleApprove({
    aiYamlHistory,
    importFromYaml,
    dispatch,
    editRoot,
    setTextContent,
    setYamlDiff,
    setOutlet,
    OutletTypes,
    customDescription,
  }) {
    return () => {
      logInfo('Approve action triggered', COMPONENT_NAME);

      if (aiYamlHistory.length > 0) {
        const latest = aiYamlHistory[aiYamlHistory.length - 1].text;
        logInfo(`Importing latest AI YAML (${latest.length} chars)`, COMPONENT_NAME);

        const { model: newModel, error } = importFromYaml(latest);       

        if (newModel) {
          logInfo('YAML import successful, updating model', COMPONENT_NAME);
          dispatch(editRoot(newModel, 0, customDescription));
          setTextContent(latest);
          setYamlDiff([]);
          dispatch(setOutlet(OutletTypes.EDITOR));
          logInfo('Model updated and switched to editor', COMPONENT_NAME);
        } else if (error) {
          logError(`YAML import failed: ${error}`, COMPONENT_NAME);
          alert(error);
        }
      } else {
        logWarn('No AI YAML history available to approve', COMPONENT_NAME);
      }
    };
  }

  static handleReject({
    setYamlDiff,
    currentModelYaml,
    setTextContent,
  }) {
    return () => {
      logInfo('Reject action triggered', COMPONENT_NAME);
      setYamlDiff([]);
      setTextContent(currentModelYaml);
      logInfo('Reverted to current model YAML', COMPONENT_NAME);
    };
  }
}

export function useSyncMonacoWithTextContent(editorRef, textContent, isEditorReady) {
  const restoreCursorPosition = useCallback((editor, position) => {
    if (!position) return;

    try {
      editor.setPosition(position);
    } catch (e) {
      logWarn(`Could not restore cursor position: ${e.message}`, COMPONENT_NAME);
      const model = editor.getModel();
      const lineCount = model.getLineCount();
      const lastLineLength = model.getLineLength(lineCount);
      editor.setPosition({
        lineNumber: lineCount,
        column: lastLineLength + 1
      });
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && isEditorReady && textContent !== undefined) {
      const currentValue = editorRef.current.getValue();
      
      if (currentValue !== textContent) {
        logInfo(`Syncing Monaco editor (${textContent.length} chars)`, COMPONENT_NAME);
        const position = editorRef.current.getPosition();
        editorRef.current.getModel().setValue(textContent);
        restoreCursorPosition(editorRef.current, position);
        editorRef.current.focus();
      }
    }
  }, [textContent, isEditorReady, editorRef, restoreCursorPosition]);
}